import { useMemo } from 'react'
import {
  GetGrupoUsuariosDominioQueryQuery,
  GetGrupoUsuariosQueryQuery
} from '@/core/types/graphql'
import { GrupoUsuariosValues } from './use-form'

interface useInitialValuesProps {
  grupoUsuarios: GetGrupoUsuariosQueryQuery['grupoUsuarios']
  usuarioLogado: GetGrupoUsuariosDominioQueryQuery['usuarioLogado']
  dealers: GetGrupoUsuariosDominioQueryQuery['dealers']
}

export const useInitialValues = ({
  grupoUsuarios,
  usuarioLogado,
  dealers
}: useInitialValuesProps) => {
  const initialValues: GrupoUsuariosValues = useMemo(() => {
    const tiposAcessoOptions = (usuarioLogado?.produtos ?? [])
      .filter(
        produto => produto.id === grupoUsuarios?.tiposAcesso?.[0]?.produto.id
      )
      .flatMap(produto =>
        produto?.tiposAcesso?.map(ta => ({
          value: ta?.id,
          label: ta?.nome
        }))
      )
    const tiposUsuariosOptions = (usuarioLogado?.tiposUsuarios ?? []).map(
      tipoUsuario => ({
        value: tipoUsuario.id,
        label: tipoUsuario.description
      })
    )
    const marcasOptions = (usuarioLogado?.marcas ?? []).map(marca => ({
      value: marca?.id,
      label: marca?.description
    }))
    return {
      active: grupoUsuarios?.active ?? true,
      cargos: grupoUsuarios?.possuiCargos
        ? grupoUsuarios.cargos?.map(cargo => ({
            value: cargo?.id,
            label: cargo?.nome
          }))
        : [],
      criador: grupoUsuarios?.criador ?? null,
      dealers: grupoUsuarios?.tiposUsuarios?.some(
        tu => tu?.id === 'DEALER_GRUPO'
      )
        ? grupoUsuarios?.todosDealers
          ? dealers?.map(dealer => ({
              value: dealer?.id,
              label: dealer?.nomeExibicao
            }))
          : grupoUsuarios?.dealers?.map(dealer => ({
              value: dealer?.id,
              label: dealer?.nomeExibicao
            }))
        : [],
      grupos: grupoUsuarios?.possuiGrupos
        ? grupoUsuarios?.grupos?.map(grupo => ({
            value: grupo?.id,
            label: grupo?.nome
          }))
        : [],
      departamentos: grupoUsuarios?.possuiDepartamentos
        ? grupoUsuarios?.departamentos?.map(departamento => ({
            value: departamento?.id,
            label: departamento?.nome
          }))
        : [],
      id: grupoUsuarios?.id,
      marcas:
        grupoUsuarios?.marcas && grupoUsuarios.marcas.length > 0
          ? grupoUsuarios?.marcas?.flatMap(marca =>
              marcasOptions?.filter(option => option?.value === marca?.id)
            )
          : usuarioLogado?.marcas?.length === 1
            ? usuarioLogado.marcas.flatMap(marca =>
                marcasOptions.filter(option => option.value === marca?.id)
              )
            : [],
      possuiCargos: grupoUsuarios?.possuiCargos ?? false,
      possuiGrupos: grupoUsuarios?.possuiGrupos ?? false,
      possuiPerfil: grupoUsuarios?.possuiPerfil ?? false,
      possuiSetores: grupoUsuarios?.possuiSetores ?? false,
      possuiUsuariosManuais: grupoUsuarios?.possuiUsuariosManuais ?? false,
      possuiDepartamentos: grupoUsuarios?.possuiDepartamentos ?? false,
      nomeOriginal: grupoUsuarios?.nome ?? '',
      nome: grupoUsuarios?.nome ?? '',
      publico: grupoUsuarios?.publico ?? true,
      setores: grupoUsuarios?.possuiSetores
        ? grupoUsuarios?.setores?.map(setor => ({
            value: setor?.id,
            label: setor?.nome
          }))
        : [],
      tipo: usuarioLogado?.tipo,
      tiposAcesso:
        grupoUsuarios?.tiposAcesso?.flatMap(tipoAcesso =>
          tiposAcessoOptions?.filter(option => option?.value === tipoAcesso?.id)
        ) ?? [],
      tiposUsuarios:
        grupoUsuarios?.tiposUsuarios?.flatMap(tipoUsuario =>
          tiposUsuariosOptions?.filter(
            option => option?.value === tipoUsuario?.id
          )
        ) ?? [],
      todosDealers: grupoUsuarios?.todosDealers ?? false,
      usuariosManuais: grupoUsuarios?.possuiUsuariosManuais
        ? grupoUsuarios?.usuariosManuais?.map(um => ({
            value: um?.id,
            label: `${um?.nome} (${um?.email})`
          }))
        : []
    }
  }, [grupoUsuarios, usuarioLogado, dealers])

  return {
    initialValues
  }
}
